import React, { useEffect, useRef, useState } from "react";

import { useTranslation } from "utils";

import { AlertBanner, Flex, ProgressBar } from "components";

const PROGRESS_COLORS = {
  INCOMPLETE: "#4d85f5",
  COMPLETE: "#065c45",
  EXCEEDED: "#db3804",
};

interface ProgressAnimatedProps {
  currentValue?: number;
  title?: string;
  description?: string;
  onAnimationFinish?: () => void;
  showPercentValue?: boolean;
  showRemainingPercentage?: boolean;
  animationDuration?: number;
  strokeColor?: string;
  strokeWidth?: number;
  errorMessage?: string;
  successMessage?: string;
}

export const ProgressBarCardWithContent = ({
  currentValue = 0,
  title = "",
  description = "",
  onAnimationFinish = () => {},
  showPercentValue = true,
  showRemainingPercentage = true,
  animationDuration = 1000,
  strokeColor,
  strokeWidth = 16,
  errorMessage = "",
  successMessage = "",
}: ProgressAnimatedProps) => {
  const { t } = useTranslation(["components"]);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [displayedValue, setDisplayedValue] = useState(currentValue);
  const previousValueRef = useRef(currentValue);

  useEffect(() => {
    if (previousValueRef.current === currentValue) {
      return;
    }

    previousValueRef.current = currentValue;

    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (currentValue === 0) {
      setDisplayedValue(0);
      onAnimationFinish();
      return;
    }

    const stepTime = animationDuration / currentValue;

    const id = setInterval(() => {
      setDisplayedValue((val) => {
        const nextVal = val + 1;
        if (nextVal >= currentValue) {
          clearInterval(id);
          return currentValue;
        }
        return nextVal;
      });
    }, stepTime);

    intervalRef.current = id;

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentValue, animationDuration]);

  useEffect(() => {
    if (displayedValue === currentValue && currentValue !== 0) {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
      }
      onAnimationFinish();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedValue]);

  const getStrokeColor = () => {
    if (strokeColor) return strokeColor;

    if (displayedValue < 100) {
      return PROGRESS_COLORS.INCOMPLETE;
    }
    if (displayedValue === 100) {
      return PROGRESS_COLORS.COMPLETE;
    }
    return PROGRESS_COLORS.EXCEEDED;
  };

  const getRemainingPercentage = () => {
    const remaining = 100 - displayedValue;
    return `${remaining}% ${t("progressBarCardWithContent.remaining")}`;
  };

  const getExceededPercentage = () => {
    const exceeded = currentValue - 100;
    return `${exceeded}% ${t("progressBarCardWithContent.overLimit")}`;
  };

  const showError = displayedValue > 100;
  const showSuccess = displayedValue === 100;

  return (
    <div>
      <Flex vertical className="padding-100 progress-bar-card-with-context">
        <Flex justify="space-between" className="margin-bottom-50">
          {title && <span className="paragraph-m-bold">{title}</span>}
          {showPercentValue && <span className="paragraph-m-bold">{`${currentValue}%`}</span>}
        </Flex>

        <ProgressBar
          percent={displayedValue > 100 ? 100 : displayedValue}
          type="line"
          showInfo={false}
          strokeColor={getStrokeColor()}
          strokeWidth={strokeWidth}
        />

        <Flex justify="space-between" className="margin-top-50">
          {showRemainingPercentage && displayedValue < 100 && <span className="paragraph-m grey-600-text">{getRemainingPercentage()}</span>}
          {showSuccess && <span className="paragraph-m">{successMessage}</span>}
          {showError && <span className="red-text paragraph-m">{getExceededPercentage()}</span>}
          {description && <span className="paragraph-m">{description}</span>}
        </Flex>

        {showError && <AlertBanner className="margin-top-200" type="warning" description={errorMessage} />}
      </Flex>
    </div>
  );
};
